import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link as MuiLink,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import { AuthService, PasswordChangeResult } from '../services/auth';
import { AdminPasswordChange } from './AdminPasswordChange';

export const AdminLoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordChangeData, setPasswordChangeData] = useState<PasswordChangeResult | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await AuthService.login(email, password);
      
      // Check if user is admin (check for both 'admin' and 'admins')
      const hasAdminAccess = result.user.groups?.includes('admin') || result.user.groups?.includes('admins') || result.user.role === 'admin';
      if (hasAdminAccess) {
        // Store token and user info (already done in AuthService.login)
        navigate('/admin/dashboard');
      } else {
        setError('Access denied. Admin privileges required.');
        await AuthService.logout();
      }
    } catch (err) {
      // Check if this is a password change requirement
      if (err && typeof err === 'object' && 'requiresPasswordChange' in err) {
        setPasswordChangeData(err as PasswordChangeResult);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChanged = (result: any) => {
    // Check if user is admin (check for both 'admin' and 'admins')
    const hasAdminAccess = result.user.groups?.includes('admin') || result.user.groups?.includes('admins') || result.user.role === 'admin';
    if (hasAdminAccess) {
      // Store token and user info
      localStorage.setItem('authToken', result.token);
      localStorage.setItem('authUser', JSON.stringify(result.user));
      navigate('/admin/dashboard');
    } else {
      setError('Access denied. Admin privileges required.');
      setPasswordChangeData(null);
    }
  };

  const handlePasswordChangeError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleBackToLogin = () => {
    setPasswordChangeData(null);
    setError(null);
    setPassword('');
  };

  // Show password change component if required
  if (passwordChangeData) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'var(--gradient-background, linear-gradient(135deg, #667eea 0%, #764ba2 100%))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2
        }}
      >
        <Container maxWidth="sm">
          <AdminPasswordChange
            passwordChangeData={passwordChangeData}
            onPasswordChanged={handlePasswordChanged}
            onError={handlePasswordChangeError}
          />
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box textAlign="center" mt={2}>
            <Button
              variant="text"
              onClick={handleBackToLogin}
              sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
            >
              ← Back to Login
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'var(--gradient-background, linear-gradient(135deg, #667eea 0%, #764ba2 100%))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Card elevation={8} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box textAlign="center" mb={4}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'var(--gradient-background, linear-gradient(135deg, #667eea 0%, #764ba2 100%))',
                  mb: 2
                }}
              >
                <AdminIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                Admin Portal
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Secure access for administrators only
              </Typography>
            </Box>

            {/* Security Notice */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 2,
                mb: 3,
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderRadius: 2,
                border: '1px solid rgba(25, 118, 210, 0.2)'
              }}
            >
              <SecurityIcon color="primary" />
              <Typography variant="body2" color="primary">
                This is a secure admin area. Only authorized personnel should access this portal.
              </Typography>
            </Box>

            {/* Login Form */}
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Admin Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                autoComplete="email"
                autoFocus
                disabled={loading}
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                autoComplete="current-password"
                disabled={loading}
                sx={{ mb: 3 }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
                sx={{
                  py: 1.5,
                  background: 'var(--gradient-background, linear-gradient(135deg, #667eea 0%, #764ba2 100%))',
                  '&:hover': {
                    opacity: 0.9
                  }
                }}
              >
                {loading ? 'Signing In...' : 'Sign In to Admin Portal'}
              </Button>
            </form>

            <Divider sx={{ my: 3 }} />

            {/* Footer Links */}
            <Box textAlign="center">
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Not an admin?
              </Typography>
              <MuiLink
                component="button"
                variant="body2"
                onClick={() => navigate('/')}
                sx={{ textDecoration: 'none' }}
              >
                Return to Guest Portal
              </MuiLink>
            </Box>

            {/* Admin Credentials Info (Development Only) */}
            {process.env.NODE_ENV === 'development' && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: 'rgba(255, 152, 0, 0.08)',
                  borderRadius: 2,
                  border: '1px solid rgba(255, 152, 0, 0.2)'
                }}
              >
                <Typography variant="caption" color="warning.main" display="block" gutterBottom>
                  <strong>Development Mode:</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Default admin credentials may be available in admin-credentials.json
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};